import React, { useMemo } from "react";
import { useDispatch } from "react-redux";
import { addFilter } from "../app/filters.js";

/**
 * Sidebar legend
 * Used to display view keys and their options, colors and filter annotations
 *
 * Recursion: 
 *  - take a select field, list its options;
 *  - for each option, check if it has a color;
 *  - check if the option has a submenu if it has display it;
 *  - if the option has a submenu, repeat the process for the submenu;
 */

// rule is:
//  - id: if exists, use it - key is a submenu;
//  - name: if no id, use name - key is a parent; (name is uniq per parent)
const filterKeyOf = (field) => field.id ?? field.name;

// Small color square next to an option to display color information
const ColorSwatch = ({ color }) => {
  if (!Array.isArray(color) || color.length < 3) return null;
  return (
    <span
      aria-hidden
      style={{
        width: 10,
        height: 10,
        borderRadius: 2,
        display: "inline-block",
        flex: "0 0 auto",
        background: `rgb(${color[0]}, ${color[1]}, ${color[2]})`,
        border: "1px solid rgba(255,255,255,0.35)",
      }}
    />
  );
};

// True if this select (or a nested submenu select) has at least one colored option.
// Loop = options of the current field. Recursion = child select of an option.
function hasColorInTree(field, keysById, visited = new Set()) {
  // get key
  const id = field.id || field.name;
  // if already visited or not a select, return false
  if (visited.has(id) || field.type !== "select") return false;
  visited.add(id);

  for (const opt of field.options || []) {
    if (Array.isArray(opt?.color) && opt.color.length >= 3) return true;
    for (const submenuId of opt.submenus || []) {
      const child = keysById[submenuId];
      if (child && hasColorInTree(child, keysById, visited)) return true;
    }
  }
  return false;
}

// Display a select field and its options. Other types (number, textarea, …) are ignored.
function LegendField({ field, keysById, filters }) {
  const dispatch = useDispatch();
  if (field.type !== "select") return null;

  const key = filterKeyOf(field);
  const selector = filters[key] ?? {};

  const toggle = (optionText, checked) => {
    dispatch(addFilter({ key, filter: { ...selector, [optionText]: checked } }));
  };

  return (
    <details
      open={hasColorInTree(field, keysById)}
      style={{ marginBottom: 4, fontSize: 12, lineHeight: "14px" }}
    >
      <summary style={{ cursor: "pointer" }}>
        <strong>{field.title || field.name}</strong>
      </summary>

      <div style={{ marginTop: 4 }}>
        {(field.options || []).map((opt) => {
          const children = (opt.submenus || [])
            .map((submenuId) => keysById[submenuId])
            .filter(Boolean);

          return (
            <div
              key={`${key}-${opt.id}`}
              style={{ marginBottom: 2, marginLeft: 6 }}
            >
              <div
                style={{ display: "flex", alignItems: "center", gap: 6, cursor: "pointer" }}
                onClick={() => toggle(opt.text, selector[opt.text] !== true)}
              >
                <input
                  type="checkbox"
                  checked={selector[opt.text] === true}
                  onClick={(e) => e.stopPropagation()}
                  onChange={(e) => toggle(opt.text, e.target.checked)}
                />
                <ColorSwatch color={opt.color} />
                <span>{opt.text}</span>
              </div>

              {children.length > 0 && (
                <div style={{ marginLeft: 14, marginTop: 2 }}>
                  {children.map((child) => (
                    <LegendField
                      key={child.id || child.name}
                      field={child}
                      keysById={keysById}
                      filters={filters}
                    />
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </details>
  );
}

// Display the legend tree
export default function FeatureLegendTree({ fields, filters }) {
  const keysById = useMemo(
    () =>
      Object.fromEntries(
        (fields || []).filter((f) => f?.id).map((f) => [f.id, f])
      ),
    [fields]
  );

  // Parent keys only (no id, or id === name). Submenus are rendered under their parent option.
  const parents = (fields || []).filter((f) => !f.id || f.id === f.name);

  return (
    <div className="filters">
      {parents.map((field) => (
        <LegendField
          key={field.id || field.name}
          field={field}
          keysById={keysById}
          filters={filters}
        />
      ))}
    </div>
  );
}
